import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum OnboardingProgressStep {
  AUTHENTICATION = 'authentication',
  PERSONAL_INFORMATION = 'personalInformation',
  PERSONAL_TRADING_INFORMATION = 'personalTradingInformation',
}

const ONBOARDING_PROGRESS_STEPS = Object.values(OnboardingProgressStep);

export enum FuturesProgressStep {
  CLIENT_CATEGORISATION = 'clientCategorisation',
  APPROPRIATENESS_TEST = 'appropriatenessTest',
}

const FUTURES_PROGRESS_STEPS = Object.values(FuturesProgressStep);

export enum InstitutionOnboardingProgressStep {
  INSTITUTION_AUTHENTICATION = 'institutionAuthentication',
  INSTITUTION_INFORMATION = 'institutionInformation',
  INSTITUTION_VERIFICATION = 'institutionVerification',
}

const INSTITUTIONAL_PROGRESS_STEPS = Object.values(InstitutionOnboardingProgressStep);

export enum InstitutionFuturesProgressStep {
  INSTITUTION_INFORMATION_FUTURES = 'institutionFuturesInstitutionInformation',
  CLIENT_CATEGORISATION = 'institutionClientCategorisation',
  APPROPRIATENESS_TEST = 'institutionFuturesAppropriatenessTest',
}

const INSTITUTIONAL_FUTURES_PROGRESS_STEPS = Object.values(InstitutionFuturesProgressStep);

export enum PeriodicReviewProgressStep {
  PERIODIC_REVIEW_PERSONAL_INFORMATION = 'periodicReviewPersonalInformation',
  PERIODIC_REVIEW_TRADING_INFORMATION = 'periodicReviewTradingInformation',
  PERIODIC_REVIEW_CONFIRMATION = 'periodicReviewConfirmation',
}

const PERIODIC_REVIEW_PROGRESS_STEPS = Object.values(PeriodicReviewProgressStep);

export type ProgressStep = OnboardingProgressStep | FuturesProgressStep | InstitutionOnboardingProgressStep | InstitutionFuturesProgressStep | PeriodicReviewProgressStep;

type ProgressStepType = {
  isActive: boolean;
  key: ProgressStep;
  progress: number;
  title: string;
};

type CurrentProgressStep = Pick<ProgressStepType, 'key' | 'progress'>;

type ProgressContextType = {
  steps: ProgressStepType[];
  setCurrentStep: (step: ProgressStep, progress?: number) => void;
  setProgress: (progress: number) => void;
};

const ProgressContext = createContext<ProgressContextType>({
  steps: [],
  setCurrentStep: () => {},
  setProgress: () => {},
});

const getProgressSteps = (currentStep: CurrentProgressStep): ProgressStep[] => {
  if (FUTURES_PROGRESS_STEPS.includes(currentStep.key as FuturesProgressStep)) return FUTURES_PROGRESS_STEPS;
  if (INSTITUTIONAL_PROGRESS_STEPS.includes(currentStep.key as InstitutionOnboardingProgressStep)) return INSTITUTIONAL_PROGRESS_STEPS;
  if (INSTITUTIONAL_FUTURES_PROGRESS_STEPS.includes(currentStep.key as InstitutionFuturesProgressStep)) return INSTITUTIONAL_FUTURES_PROGRESS_STEPS;
  if (PERIODIC_REVIEW_PROGRESS_STEPS.includes(currentStep.key as PeriodicReviewProgressStep)) return PERIODIC_REVIEW_PROGRESS_STEPS;

  return ONBOARDING_PROGRESS_STEPS;
};

export const ProgressProvider = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<CurrentProgressStep | null>(null);
  const steps = useMemo<ProgressStepType[]>(() => {
    if (!currentStep) return [];

    const inferredSteps = getProgressSteps(currentStep);
    const currentIndex = inferredSteps.indexOf(currentStep.key);

    return inferredSteps.map((key, index) => ({
      isActive: key === currentStep.key,
      key,
      progress: index < currentIndex ? 1 : index === currentIndex ? currentStep.progress : 0,
      title: t(`progress.${key}`),
    }));
  }, [currentStep, t]);

  const resetCurrentStep = useCallback(() => setCurrentStep(null), []);

  const handleSetCurrentStep: ProgressContextType['setCurrentStep'] = useCallback(
    (step, progress = 0) => {
      if (progress < 0 || progress > 1) throw new Error('Progress must be between 0 and 1');

      setCurrentStep({ key: step, progress });

      return resetCurrentStep;
    },
    [resetCurrentStep],
  );

  const handleSetProgress = useCallback((progress: number) => {
    if (progress < 0 || progress > 1) throw new Error('Progress must be between 0 and 1');

    setCurrentStep((currentStep) => (currentStep ? { key: currentStep.key, progress } : null));
  }, []);

  const value: ProgressContextType = {
    steps,
    setCurrentStep: handleSetCurrentStep,
    setProgress: handleSetProgress,
  };

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
};
ProgressProvider.displayName = 'ProgressProvider';

export const useProgress = () => useContext(ProgressContext);
